import { MagnifierMinusIcon, MagnifierPlusIcon } from "@/assets/icons";
import { Button } from "@/components/button";
import {
  usePlaybackMediator,
  useTimelinePlayback,
} from "@/contexts/playback-context";
import { useSelector } from "@legendapp/state/react";

import { ImportButton } from "./import-button";
import { DateTimeRangePicker } from "./date-time-range-picker";
import { LiveButton } from "./live-button";
import { TimelinePlayButton } from "./play-button";
import { CurrentTime } from "./current-time";
import { SkipToVideoStart } from "./skip-to-video-start";
import { SkipToNextTrack } from "./skip-to-next-track";
import { ControlEvent, emitter } from "@/services/event-emitter";
import { Flex, Slider, Text } from "@radix-ui/themes";
import { LayoutInfoButton } from "./layout-info-button";
import { SeekAmountInput } from "./seek-amount-input";
import { useHotkeys } from "react-hotkeys-hook";
import {
  hiddenTracks$,
  selectedBin$,
  visibleTracksSnapshots$,
} from "@/virtual-store/state";
import { useTracksBuffer } from "@/services/buffer-controller";

export function BasicControls() {
  const timelinePlayback = useTimelinePlayback();
  const mediator = usePlaybackMediator();
  const timelineState = useSelector(() => timelinePlayback.playbackState.get());

  const onPlayPause = () => {
    const timelineState = timelinePlayback.playbackState.peek();

    if (timelineState === "playing" || timelineState === "pending") {
      mediator.pause();
    } else {
      mediator.play();
    }
  };

  useHotkeys("space", onPlayPause, {
    preventDefault: true,
  });

  useHotkeys(
    "left",
    () => {
      mediator.seekBackward();
    },
    {
      preventDefault: true,
    }
  );

  useHotkeys(
    "right",
    () => {
      mediator.seekForward();
    },
    {
      preventDefault: true,
    }
  );

  useHotkeys(
    "mod+left",
    (e) => {
      if (selectedBin$.peek() !== null) return;

      e.stopImmediatePropagation();
      mediator.stepBackward();
    },
    {
      preventDefault: true,
    }
  );

  useHotkeys(
    "mod+right",
    (e) => {
      if (selectedBin$.peek() !== null) return;

      e.stopImmediatePropagation();
      mediator.stepForward();
    },
    {
      preventDefault: true,
    }
  );

  return (
    <div className="flex justify-end items-center gap-4 border-none border-[var(--gray-a7)] px-4 h-14">
      <SeekAmountInput />
      <SkipToNextTrack />
      <SkipToVideoStart />
      <TimelinePlayButton timelineState={timelineState} onClick={onPlayPause} />
      <CurrentTime />
      <LiveButton />
      <ZoomSlider />
      <DateTimeRangePicker />
      <ImportButton />
      <LayoutInfoButton />
    </div>
  );
}

export function TracksCountInfo() {
  const [tracksBuffer] = useTracksBuffer();
  const totalTrackCount = useSelector(
    () => Object.keys(tracksBuffer.tracksBufferSizeMap.get()).length
  );
  const activeTimestampTrackCount = useSelector(() => {
    const hiddenTracks = hiddenTracks$.get();
    const visibleTracksSnapshots = visibleTracksSnapshots$.get();

    if (!visibleTracksSnapshots) return [];

    const entries = [...visibleTracksSnapshots.entries()];
    const info = entries.filter(([trackId]) => !hiddenTracks.has(trackId));

    return info.length;
  });

  return (
    <Flex
      className="absolute bottom-2 left-2 bg-[var(--gray-2)] py-2 px-3 rounded-xl"
      align="center"
    >
      <Text size="1">
        Total track count: {totalTrackCount} <span className="mx-2">•</span>
      </Text>
      <Text size="1">
        Active timestamp track count: {activeTimestampTrackCount}
      </Text>
    </Flex>
  );
}

export function ZoomSlider() {
  const timelinePlayback = useTimelinePlayback();
  const ZOOM_STEP = 0.05;

  const zoomLevel = useSelector(() => timelinePlayback.zoomLevel.get());

  return (
    <>
      <Button
        onClick={() => {
          const currentZoom = timelinePlayback.zoomLevel.peek();
          const newZoom = Math.max(currentZoom - ZOOM_STEP, 0);

          if (newZoom < currentZoom) {
            emitter.emit(ControlEvent.ZoomChanged, {
              zoomLevel: newZoom,
            });
          }
        }}
      >
        <MagnifierMinusIcon className="text-brand-gray-100" />
      </Button>
      <Slider
        className="relative flex items-center select-none touch-none max-w-[200px] h-5"
        defaultValue={[25]}
        max={1}
        min={0}
        step={ZOOM_STEP}
        color="gray"
        highContrast
        size="1"
        value={[zoomLevel]}
        onValueChange={([value]) => {
          emitter.emit(ControlEvent.ZoomChanged, {
            zoomLevel: value,
          });
        }}
        onKeyDown={(e) => {
          e.preventDefault();
        }}
        onPointerUp={(e) => {
          (e.target as HTMLElement).blur();
        }}
      />
      <Button
        onClick={() => {
          const currentZoom = timelinePlayback.zoomLevel.peek();
          const newZoom = Math.min(currentZoom + ZOOM_STEP, 1);

          if (newZoom > currentZoom) {
            emitter.emit(ControlEvent.ZoomChanged, {
              zoomLevel: newZoom,
            });
          }
        }}
      >
        <MagnifierPlusIcon className="text-brand-gray-100" />
      </Button>
    </>
  );
}
